/**
 * Fordonsplacering – adress och koordinat (som utlämningsplats, men en punkt per fordon).
 */

import { useState, useCallback } from 'react';
import { MapIcon, MapPinIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { geocodeSearch } from '@/services/travel/travelPlannerService';
import { reverseGeocode } from '@/services/geocoding/geocodingApi';
import PlacementPointMapModal from './PlacementPointMapModal';

export interface PlacementFormState {
  address: string;
  postalCode: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
}

export interface VehicleGpsPosition {
  lat: number;
  lng: number;
  timestamp?: string;
}

interface VehiclePlacementTabProps {
  value: PlacementFormState;
  onChange: (next: PlacementFormState) => void;
  hasChanges?: boolean;
  lastPosition?: VehicleGpsPosition | null;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
}

export default function VehiclePlacementTab({
  value,
  onChange,
  hasChanges,
  lastPosition,
  onSave,
  onCancel,
  isSaving,
}: VehiclePlacementTabProps) {
  const { t } = useTranslation();
  const [mapOpen, setMapOpen] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [fromGps, setFromGps] = useState(false);

  const update = useCallback(
    (patch: Partial<PlacementFormState>) => onChange({ ...value, ...patch }),
    [onChange, value]
  );

  const handleGeocodeAddress = async () => {
    const parts = [value.address, value.postalCode, value.city].filter((p) => p?.trim());
    if (parts.length === 0) {
      toast.error(t('vehicleCard.placement.addressRequired'));
      return;
    }
    setGeocoding(true);
    try {
      const results = await geocodeSearch(parts.join(', '), 1);
      if (!results.length) {
        toast.error(t('vehicleCard.placement.geocodeNotFound'));
        return;
      }
      const hit = results[0];
      update({
        latitude: hit.lat,
        longitude: hit.lon,
      });
      toast.success(t('vehicleCard.placement.geocodeSuccess'));
    } catch {
      toast.error(t('vehicleCard.placement.geocodeError'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleMapSave = (
    lat: number,
    lng: number,
    addr?: { address?: string; postalCode?: string; city?: string }
  ) => {
    update({
      latitude: lat,
      longitude: lng,
      ...(addr?.address ? { address: addr.address } : {}),
      ...(addr?.postalCode ? { postalCode: addr.postalCode } : {}),
      ...(addr?.city ? { city: addr.city } : {}),
    });
  };

  const handleClear = () => {
    onChange({
      address: '',
      postalCode: '',
      city: '',
      latitude: null,
      longitude: null,
    });
  };

  const handleFromGps = async () => {
    const lat = lastPosition?.lat;
    const lng = lastPosition?.lng;
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast.error(t('vehicleCard.placement.fromGpsMissing'));
      return;
    }
    setFromGps(true);
    try {
      const addr = await reverseGeocode(lat, lng);
      onChange({
        latitude: lat,
        longitude: lng,
        address:
          addr?.street && addr?.houseNumber
            ? `${addr.street} ${addr.houseNumber}`
            : addr?.address ?? value.address,
        postalCode: addr?.postcode ?? value.postalCode,
        city: addr?.city ?? value.city,
      });
      toast.success(t('vehicleCard.placement.fromGpsSuccess'));
    } catch {
      onChange({
        ...value,
        latitude: lat,
        longitude: lng,
      });
      toast.error(t('vehicleCard.placement.fromGpsError'));
    } finally {
      setFromGps(false);
    }
  };

  return (
    <div
      className={`space-y-4 p-4 border rounded-lg ${
        hasChanges ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/20' : 'border-border'
      }`}
    >
      <p className="text-sm text-textSecondary">{t('vehicleCard.placement.description')}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs text-textSecondary mb-1">{t('booking.address')}</label>
          <input
            type="text"
            value={value.address}
            onChange={(e) => update({ address: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
            maxLength={255}
          />
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('booking.postalCode')}</label>
          <input
            type="text"
            value={value.postalCode}
            onChange={(e) => update({ postalCode: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
            maxLength={20}
          />
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">{t('booking.city')}</label>
          <input
            type="text"
            value={value.city}
            onChange={(e) => update({ city: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
            maxLength={100}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleGeocodeAddress()}
          disabled={geocoding}
          className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-bgBase disabled:opacity-50"
        >
          {geocoding ? t('common.loading') : t('vehicleCard.placement.geocodeFromAddress')}
        </button>
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-bgBase"
        >
          <MapIcon className="w-4 h-4" />
          {t('vehicleCard.placement.pickOnMap')}
        </button>
        <button
          type="button"
          onClick={() => void handleFromGps()}
          disabled={fromGps}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-bgBase disabled:opacity-50"
        >
          <MapPinIcon className="w-4 h-4" />
          {fromGps ? t('common.loading') : t('vehicleCard.placement.fromGps')}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="px-3 py-1.5 text-sm text-textSecondary hover:text-textDefault"
        >
          {t('vehicleCard.placement.clear')}
        </button>
      </div>

      {value.latitude != null && value.longitude != null && (
        <p className="text-sm text-textSecondary">
          {t('vehicleCard.placement.coordinates')}: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
        </p>
      )}

      {lastPosition?.timestamp && (
        <p className="text-xs text-textSecondary">
          {t('vehicleCard.placement.gpsTime', {
            time: new Date(lastPosition.timestamp).toLocaleString(),
          })}
        </p>
      )}

      {hasChanges && onSave && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-bgBase disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      )}

      {mapOpen && (
        <PlacementPointMapModal
          initialLat={value.latitude}
          initialLng={value.longitude}
          onSave={handleMapSave}
          onClose={() => setMapOpen(false)}
        />
      )}
    </div>
  );
}
